import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ClerkFailed, ClerkProvider, useAuth as useClerkAuth, useClerk, useUser } from '@clerk/react'
import { AuthContext } from './authContextObject'
import { fetchAuthBootstrap, profileAPI, setAccessTokenGetter } from '../lib/api'
import analyticsService from '../services/analyticsService'

// Clerk holds the login only (WO-15, SoT 5.8). Our API maps the Clerk session to our own user id
// and returns profile + preferences from our Postgres (/api/currency/bootstrap).
const PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || ''

// Matches the site palette (tailwind: dark #16161A, purple #6A66A3, cream #DDD8B8).
export const clerkAppearance = {
  variables: {
    colorPrimary: '#6A66A3',
    colorPrimaryForeground: '#DDD8B8',
    colorBackground: '#16161A',
    colorForeground: '#DDD8B8',
    colorMutedForeground: 'rgba(221, 216, 184, 0.7)',
    colorInput: '#16161A',
    colorInputForeground: '#DDD8B8',
    colorNeutral: '#DDD8B8',
    borderRadius: '0.5rem',
  },
  elements: {
    cardBox: { border: '1px solid rgba(106, 102, 163, 0.3)', boxShadow: 'none' },
  },
}

const notAvailable = async () => ({
  data: null,
  error: new Error('Sign-in is temporarily unavailable. Please try again later.'),
})

const buildUser = (account, clerkUser) => {
  const hasGoogle = (clerkUser?.externalAccounts || []).some((acc) =>
    String(acc?.provider || '').includes('google')
  )
  const hasPassword = Boolean(clerkUser?.passwordEnabled)
  const fullName = clerkUser?.fullName || [clerkUser?.firstName, clerkUser?.lastName].filter(Boolean).join(' ')
  return {
    id: account.id,
    email: account.email || clerkUser?.primaryEmailAddress?.emailAddress || null,
    provider: 'clerk',
    providerUserId: clerkUser?.id || null,
    created_at: clerkUser?.createdAt || null,
    user_metadata: {
      full_name: fullName || '',
      name: fullName || '',
      avatar_url: clerkUser?.imageUrl || '',
      picture: clerkUser?.imageUrl || '',
    },
    app_metadata: { provider: hasGoogle && !hasPassword ? 'google' : 'email' },
    identities: hasGoogle ? [{ provider: 'google' }] : [],
    hasPassword,
  }
}

// No publishable key in this build: keep every page working signed-out and say so on /login.
function AuthUnavailableProvider({ children }) {
  const value = useMemo(() => ({
    user: null,
    profile: null,
    preferredCurrency: null,
    setPreferredCurrency: () => {},
    session: null,
    loading: false,
    accountError: null,
    authProvider: 'clerk',
    authUnavailable: true,
    signUp: notAvailable,
    signIn: notAvailable,
    signInWithGoogle: notAvailable,
    signOut: async () => ({ error: null }),
    updateProfile: notAvailable,
    loadUserProfile: async () => {},
    clearAuthState: async () => {},
    changePassword: notAvailable,
    sendPasswordReset: notAvailable,
    openAccountSettings: () => {},
  }), [])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// If Clerk cannot load (outage, blocked script), the site must keep working signed-out.
const CLERK_LOAD_TIMEOUT_MS = 15000

function FailureSignal({ onFail }) {
  useEffect(() => {
    onFail()
  }, [onFail])
  return null
}

function ClerkBridge({ children }) {
  const { isLoaded, isSignedIn, getToken } = useClerkAuth()
  const { user: clerkUser } = useUser()
  const clerk = useClerk()

  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [preferredCurrency, setPreferredCurrency] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accountError, setAccountError] = useState(null)
  const [clerkFailed, setClerkFailed] = useState(false)
  const markClerkFailed = useCallback(() => setClerkFailed(true), [])
  const trackedSignIn = useRef(null)
  const clerkUserRef = useRef(clerkUser)
  clerkUserRef.current = clerkUser
  // getToken() returns a fresh (auto-refreshed) session token; keep a ref so effects do not re-run.
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const clearAuthState = useCallback(async () => {
    setUser(null)
    setProfile(null)
    setPreferredCurrency(null)
    setAccountError(null)
  }, [])

  const loadUserProfile = useCallback(async () => {
    const bootstrap = await fetchAuthBootstrap()
    if (!bootstrap?.user?.id) {
      setUser(null)
      setProfile(null)
      setPreferredCurrency(null)
      setAccountError(
        bootstrap?.status === 403
          ? 'This account has been suspended.'
          : 'We could not load your account. Please try again in a moment.'
      )
      return null
    }
    const nextUser = buildUser(bootstrap.user, clerkUserRef.current)
    setAccountError(null)
    setUser(nextUser)
    setProfile(bootstrap.profile || null)
    setPreferredCurrency(bootstrap.preferred_currency || null)
    return nextUser
  }, [])

  const clerkUserId = clerkUser?.id || null

  useEffect(() => {
    if (isLoaded) return
    const timer = setTimeout(() => {
      console.warn('Clerk did not load in time: continuing signed-out.')
      setClerkFailed(true)
    }, CLERK_LOAD_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [isLoaded])

  useEffect(() => {
    if (!isLoaded) return
    let cancelled = false

    const sync = async () => {
      if (!isSignedIn) {
        setAccessTokenGetter(null)
        await clearAuthState()
        analyticsService.setUserId(null, { emitIdentityLink: false })
        trackedSignIn.current = null
        setLoading(false)
        return
      }
      setAccessTokenGetter(() => getTokenRef.current())
      setLoading(true)
      const nextUser = await loadUserProfile()
      if (cancelled) return
      if (nextUser && trackedSignIn.current !== nextUser.id) {
        trackedSignIn.current = nextUser.id
        const createdAt = clerkUserRef.current?.createdAt ? new Date(clerkUserRef.current.createdAt).getTime() : 0
        const meta = { provider: nextUser.app_metadata.provider }
        const track = Date.now() - createdAt < 10 * 60 * 1000
          ? analyticsService.trackAuthSignedUp(nextUser.id, meta)
          : analyticsService.trackAuthLoggedIn(nextUser.id, meta)
        Promise.resolve(track).catch((err) => console.warn('Analytics tracking failed:', err))
      }
      setLoading(false)
    }

    sync()
    return () => {
      cancelled = true
    }
  }, [isLoaded, isSignedIn, clerkUserId, loadUserProfile, clearAuthState])

  const signOut = useCallback(async () => {
    try {
      setLoading(true)
      await clerk.signOut()
      setAccessTokenGetter(null)
      await clearAuthState()
      return { error: null }
    } catch (error) {
      console.error('Sign out error:', error)
      setAccessTokenGetter(null)
      await clearAuthState()
      return { error }
    } finally {
      setLoading(false)
    }
  }, [clerk, clearAuthState])

  const updateProfile = useCallback(async (updates) => {
    if (!user) return { data: null, error: new Error('No user logged in') }
    const { data, error } = await profileAPI.updateMyProfile(updates)
    if (error) return { data: null, error }
    setProfile(data)
    return { data, error: null }
  }, [user])

  // Sign-up, sign-in, Google and password reset are handled by Clerk's <SignIn>/<SignUp> on /login.
  const openSignIn = useCallback(async () => {
    clerk.redirectToSignIn()
    return { data: null, error: null }
  }, [clerk])

  const openAccountSettings = useCallback(() => {
    clerk.openUserProfile()
  }, [clerk])

  // Clerk's account panel handles password changes (with re-verification when Clerk asks for it).
  const changePassword = useCallback(async () => {
    clerk.openUserProfile()
    return { data: null, error: null }
  }, [clerk])

  // Clerk's reset flow ("Forgot password?" on sign-in) emails a code to the address on the account.
  const sendPasswordReset = useCallback(async () => {
    await clerk.signOut({ redirectUrl: '/login' })
    return { data: null, error: null }
  }, [clerk])

  const value = {
    user,
    profile,
    preferredCurrency,
    setPreferredCurrency,
    session: isSignedIn ? { provider: 'clerk' } : null,
    loading: clerkFailed && !isLoaded ? false : (!isLoaded || loading),
    accountError,
    authProvider: 'clerk',
    authUnavailable: clerkFailed && !isLoaded,
    clerkSignedIn: Boolean(isSignedIn),
    signUp: openSignIn,
    signIn: openSignIn,
    signInWithGoogle: openSignIn,
    signOut,
    updateProfile,
    loadUserProfile,
    clearAuthState,
    changePassword,
    sendPasswordReset,
    openAccountSettings,
  }

  return (
    <AuthContext.Provider value={value}>
      <ClerkFailed>
        <FailureSignal onFail={markClerkFailed} />
      </ClerkFailed>
      {children}
    </AuthContext.Provider>
  )
}

export default function ClerkAuthProvider({ children }) {
  const navigate = useNavigate()

  if (!PUBLISHABLE_KEY) {
    if (process.env.NODE_ENV !== 'test') {
      console.warn('REACT_APP_CLERK_PUBLISHABLE_KEY is not set: sign-in is disabled in this build.')
    }
    return <AuthUnavailableProvider>{children}</AuthUnavailableProvider>
  }

  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/login"
      signUpUrl="/signup"
      signInFallbackRedirectUrl="/auth/callback"
      signUpFallbackRedirectUrl="/auth/callback"
      afterSignOutUrl="/"
      appearance={clerkAppearance}
    >
      <ClerkBridge>{children}</ClerkBridge>
    </ClerkProvider>
  )
}
